import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import {
  Search, LayoutDashboard, ShoppingCart, Receipt, Package, BarChart3,
  Users, FileText, Settings, CreditCard, Truck, UserCheck, Shield,
  TrendingUp, Activity, BookOpen, Zap, ArrowRight, Clock, Hash,
  Building2, Wallet, Scale, Factory, ChefHat, Briefcase
} from 'lucide-react';
import api from '@/lib/api';
import { NAV_GROUPS, ROUTE_MODULE_MAP } from '@/config/navigationConfig';

const MODULE_ICONS = {
  admin: LayoutDashboard,
  sales: ShoppingCart,
  expenses: Receipt,
  pos: Package,
  hr: Users,
  accounting: BookOpen,
  reports: BarChart3,
  factory: Factory,
};

const QUICK_ACTIONS = [
  { id: 'new-sale', label: 'New Sale', path: '/pos', icon: ShoppingCart, keywords: 'add create sale quick entry' },
  { id: 'new-expense', label: 'New Expense', path: '/expenses', icon: Receipt, keywords: 'add create expense' },
  { id: 'new-invoice', label: 'New Invoice', path: '/invoices', icon: FileText, keywords: 'add create invoice' },
  { id: 'new-employee', label: 'New Employee', path: '/employees', icon: UserCheck, keywords: 'add create employee hire' },
  { id: 'new-journal', label: 'New Journal Entry', path: '/journal-entries', icon: BookOpen, keywords: 'add create journal entry accounting' },
];

export function CommandPalette({ open, onOpenChange }) {
  const [query, setQuery] = useState('');
  const [activeSection, setActiveSection] = useState('pages');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dataResults, setDataResults] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  // Flatten all nav items from NAV_GROUPS for page search
  const allPages = NAV_GROUPS.flatMap(group =>
    group.items.map(item => ({
      ...item,
      group: group.label,
      module: group.module,
    }))
  );

  // Filter pages by query
  const filteredPages = query.trim()
    ? allPages.filter(p =>
        p.label.toLowerCase().includes(query.toLowerCase()) ||
        p.path.toLowerCase().includes(query.toLowerCase()) ||
        p.group.toLowerCase().includes(query.toLowerCase())
      )
    : allPages.slice(0, 12);

  // Filter quick actions by query
  const filteredActions = query.trim()
    ? QUICK_ACTIONS.filter(a =>
        a.label.toLowerCase().includes(query.toLowerCase()) ||
        a.keywords.toLowerCase().includes(query.toLowerCase())
      )
    : QUICK_ACTIONS;

  // Data search (debounced)
  const searchData = useCallback(async (q) => {
    if (!q || q.length < 2) { setDataResults([]); return; }
    setDataLoading(true);
    try {
      const results = [];
      const [customersRes, suppliersRes, salesRes] = await Promise.allSettled([
        api.get(`/customers?search=${encodeURIComponent(q)}&limit=5`),
        api.get(`/suppliers?search=${encodeURIComponent(q)}&limit=5`),
        api.get(`/sales?search=${encodeURIComponent(q)}&limit=5`),
      ]);
      if (customersRes.status === 'fulfilled') {
        const custs = customersRes.value.data?.customers || customersRes.value.data || [];
        (Array.isArray(custs) ? custs : []).slice(0, 3).forEach(c => {
          results.push({ type: 'Customer', label: c.name, sub: c.phone || c.email || '', path: '/customers', icon: Users });
        });
      }
      if (suppliersRes.status === 'fulfilled') {
        const supps = suppliersRes.value.data?.suppliers || suppliersRes.value.data || [];
        (Array.isArray(supps) ? supps : []).slice(0, 3).forEach(s => {
          results.push({ type: 'Supplier', label: s.name, sub: s.phone || s.contact_person || '', path: '/suppliers', icon: Truck });
        });
      }
      setDataResults(results);
    } catch {
      setDataResults([]);
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length >= 2) {
      debounceRef.current = setTimeout(() => searchData(query), 400);
    } else {
      setDataResults([]);
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, searchData]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setActiveSection('pages');
      setDataResults([]);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Get all selectable items for keyboard nav
  const allItems = [
    ...filteredActions.map(a => ({ ...a, section: 'actions' })),
    ...filteredPages.map(p => ({ ...p, section: 'pages' })),
    ...dataResults.map(d => ({ ...d, section: 'data' })),
  ];

  const handleSelect = (item) => {
    onOpenChange(false);
    navigate(item.path);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (allItems[selectedIndex]) handleSelect(allItems[selectedIndex]);
    }
  };

  useEffect(() => { setSelectedIndex(0); }, [query]);

  let itemCounter = 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden rounded-2xl border-border/80 shadow-2xl" data-testid="command-palette">
        <DialogTitle className="sr-only">Command Palette</DialogTitle>
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-white">
          <Search size={18} className="text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, actions, customers, suppliers..."
            className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground text-foreground"
            data-testid="command-search-input"
          />
          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono text-muted-foreground border border-border">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[420px] overflow-y-auto" data-testid="command-results">
          {/* Quick Actions */}
          {filteredActions.length > 0 && (
            <div className="px-2 py-2">
              <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Quick Actions</p>
              {filteredActions.map((action) => {
                const idx = itemCounter++;
                const Icon = action.icon;
                return (
                  <button
                    key={action.id}
                    onClick={() => handleSelect(action)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      selectedIndex === idx ? 'bg-primary/8 text-primary' : 'text-foreground hover:bg-muted/50'
                    }`}
                    data-testid={`cmd-action-${action.id}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      selectedIndex === idx ? 'bg-primary/15' : 'bg-muted'
                    }`}>
                      <Icon size={15} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{action.label}</p>
                    </div>
                    <Zap size={12} className="text-warning shrink-0" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Pages */}
          {filteredPages.length > 0 && (
            <div className="px-2 py-2 border-t border-stone-50">
              <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Pages {query && `(${filteredPages.length})`}
              </p>
              {filteredPages.slice(0, 15).map((page) => {
                const idx = itemCounter++;
                const Icon = page.icon;
                const ModuleIcon = MODULE_ICONS[page.module] || LayoutDashboard;
                return (
                  <button
                    key={page.path}
                    onClick={() => handleSelect(page)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      selectedIndex === idx ? 'bg-primary/8 text-primary' : 'text-foreground hover:bg-muted/50'
                    }`}
                    data-testid={`cmd-page-${page.path.slice(1)}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      selectedIndex === idx ? 'bg-primary/15' : 'bg-muted'
                    }`}>
                      <Icon size={15} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{page.label}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{page.group}</p>
                    </div>
                    <ArrowRight size={12} className="text-muted-foreground shrink-0" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Data Results */}
          {(dataResults.length > 0 || dataLoading) && (
            <div className="px-2 py-2 border-t border-stone-50">
              <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Data {dataLoading && <span className="animate-pulse">searching...</span>}
              </p>
              {dataResults.map((item, i) => {
                const idx = itemCounter++;
                const Icon = item.icon;
                return (
                  <button
                    key={`data-${i}`}
                    onClick={() => handleSelect(item)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      selectedIndex === idx ? 'bg-primary/8 text-primary' : 'text-foreground hover:bg-muted/50'
                    }`}
                    data-testid={`cmd-data-${i}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      selectedIndex === idx ? 'bg-primary/15' : 'bg-muted'
                    }`}>
                      <Icon size={15} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.label}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{item.type} {item.sub && `· ${item.sub}`}</p>
                    </div>
                    <ArrowRight size={12} className="text-muted-foreground shrink-0" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {query && filteredPages.length === 0 && filteredActions.length === 0 && dataResults.length === 0 && !dataLoading && (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground" data-testid="cmd-empty">
              <Search size={28} className="mb-2 opacity-30" />
              <p className="text-sm">No results for "{query}"</p>
              <p className="text-xs mt-1">Try a different search term</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/50 text-[11px] text-muted-foreground">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-muted/80 rounded text-[10px] font-mono">↑↓</kbd> Navigate</span>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-muted/80 rounded text-[10px] font-mono">↵</kbd> Open</span>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-muted/80 rounded text-[10px] font-mono">Esc</kbd> Close</span>
          </div>
          <span className="font-medium">Smarto Command Palette</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
